import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { rupiah, monthLabel, dateLabel, ATTEND_STATUS, PAYMENT_STATUS, UNDERSTANDING, DAYS } from "@/lib/format";
import { Logo } from "@/components/Logo";
import { Avatar } from "@/components/PhotoUpload";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Kwitansi } from "@/components/PrintDocs";
import { CalendarDays, ClipboardCheck, BookOpen, Wallet, Loader2, Printer, Star, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAV = [["jadwal", "Jadwal", CalendarDays], ["kehadiran", "Kehadiran", ClipboardCheck], ["jurnal", "Jurnal", BookOpen], ["tagihan", "Tagihan", Wallet]];

export default function PublicPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/portal/${token}`).then((r) => setData(r.data)).catch(() => setErr("Portal tidak ditemukan. Periksa kembali tautan Anda."));
  }, [token]);
  useEffect(() => { if (data && window.location.hash) document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ behavior: "smooth" }); }, [data]);

  if (err) return <div className="min-h-screen flex items-center justify-center bg-amber-50 p-6 text-center text-slate-600" data-testid="portal-error">{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-amber-50"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;
  const { student, recap, journals, payments, settings, attendance, reschedules } = data;
  const slots = [...(student.schedule || [])].sort((a, b) => DAYS.indexOf(a.day) - DAYS.indexOf(b.day));

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50/40" data-testid="public-portal">
      <header className="no-print bg-white/80 backdrop-blur border-b border-amber-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Logo size="sm" />
          <nav className="flex gap-1 overflow-x-auto">{NAV.map(([id, label, Icon]) => <a key={id} href={`#${id}`} data-testid={`portal-nav-${id}`} className="flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-orange-600 px-2.5 py-1.5 rounded-lg hover:bg-orange-50 whitespace-nowrap"><Icon className="w-3.5 h-3.5" />{label}</a>)}</nav>
        </div>
      </header>

      <main className="no-print max-w-3xl mx-auto px-4 py-6 space-y-6">
        <Card className="p-5 rounded-2xl border-amber-100 flex items-center gap-4" data-testid="portal-student">
          <Avatar src={student.photo} name={student.name} size="w-16 h-16" rounded="rounded-2xl" text="text-2xl" />
          <div className="flex-1">
            <div className="text-xs text-orange-500 font-semibold">Portal Orang Tua</div>
            <h1 className="font-display text-2xl font-bold text-slate-900">{student.name}</h1>
            <div className="text-sm text-slate-500">{student.class_label || student.level} · <span className={student.status === "aktif" ? "text-emerald-600" : "text-slate-400"}>{student.status === "aktif" ? "Aktif" : "Nonaktif"}</span></div>
          </div>
        </Card>

        <section id="jadwal" className="scroll-mt-20">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-2 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-sky-500" /> Jadwal Belajar</h2>
          <Card className="rounded-2xl border-amber-100 divide-y divide-amber-50">
            {slots.length === 0 && <p className="p-4 text-sm text-slate-400">Belum ada jadwal.</p>}
            {slots.map((s, i) => <div key={i} className="flex justify-between px-4 py-3 text-sm"><span className="font-semibold">{s.day}</span><span className="text-orange-600 font-bold">{s.time} · {s.duration || 60} menit</span></div>)}
          </Card>
          {reschedules.filter((r) => r.new_date >= data.month.slice(0, 7)).slice(0, 3).map((r) => (
            <div key={r.id} className="mt-2 text-xs bg-violet-50 border border-violet-100 rounded-xl px-3 py-2 text-violet-700">Jadwal pengganti: <s className="text-slate-400">{dateLabel(r.original_date)}</s> → <b>{dateLabel(r.new_date)} {r.new_time}</b></div>
          ))}
        </section>

        <section id="kehadiran" className="scroll-mt-20">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-2 flex items-center gap-2"><ClipboardCheck className="w-5 h-5 text-emerald-500" /> Rekap Kehadiran · {monthLabel(data.month)}</h2>
          <div className="grid grid-cols-5 gap-2 mb-3">
            {[["Hadir", recap.hadir, "text-emerald-600"], ["Izin", recap.izin, "text-sky-600"], ["Sakit", recap.sakit, "text-amber-600"], ["Alpa", recap.alpa, "text-rose-600"], ["%", `${recap.pct}%`, "text-orange-600"]].map(([k, v, c]) => (
              <Card key={k} className="p-2 rounded-xl border-amber-100 text-center"><div className={`font-display text-xl font-bold ${c}`}>{v}</div><div className="text-[10px] text-slate-400">{k}</div></Card>
            ))}
          </div>
          <Card className="rounded-2xl border-amber-100 divide-y divide-amber-50 max-h-64 overflow-y-auto">
            {attendance.length === 0 && <p className="p-4 text-sm text-slate-400">Belum ada data kehadiran.</p>}
            {attendance.slice(0, 20).map((a) => <div key={a.id} className="flex justify-between items-center px-4 py-2 text-sm"><span>{dateLabel(a.date)}</span><Badge variant="outline" className={`rounded-full text-[10px] ${ATTEND_STATUS[a.status]?.cls}`}>{ATTEND_STATUS[a.status]?.label}</Badge></div>)}
          </Card>
        </section>

        <section id="jurnal" className="scroll-mt-20">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-2 flex items-center gap-2"><BookOpen className="w-5 h-5 text-violet-500" /> Laporan Belajar</h2>
          <div className="space-y-2">
            {journals.length === 0 && <Card className="p-4 rounded-2xl border-amber-100 text-sm text-slate-400">Belum ada laporan belajar.</Card>}
            {journals.slice(0, 15).map((j) => (
              <Card key={j.id} className="p-4 rounded-2xl border-amber-100" data-testid={`portal-journal-${j.id}`}>
                <div className="flex justify-between items-start gap-2">
                  <div><div className="text-xs text-slate-400">{dateLabel(j.date)}</div><div className="font-semibold text-slate-800">{j.material}</div></div>
                  <span className="flex shrink-0">{[1,2,3,4,5].map((i) => <Star key={i} className={`w-4 h-4 ${i <= j.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-2 text-xs">
                  <Badge variant="outline" className={`rounded-full ${UNDERSTANDING[j.understanding || "paham"].cls}`}>{UNDERSTANDING[j.understanding || "paham"].label}</Badge>
                  {j.homework && <span className="text-slate-500">PR: {j.homework}</span>}
                </div>
                {j.teacher_note && <p className="text-xs text-slate-500 italic mt-2">"{j.teacher_note}"</p>}
              </Card>
            ))}
          </div>
        </section>

        <section id="tagihan" className="scroll-mt-20">
          <span id="kwitansi" className="block scroll-mt-20" />
          <h2 className="font-display text-lg font-bold text-slate-900 mb-2 flex items-center gap-2"><Wallet className="w-5 h-5 text-orange-500" /> Status Tagihan</h2>
          <div className="space-y-2">
            {payments.length === 0 && <Card className="p-4 rounded-2xl border-amber-100 text-sm text-slate-400">Belum ada tagihan.</Card>}
            {payments.map((p) => (
              <Card key={p.id} className="p-4 rounded-2xl border-amber-100 flex flex-wrap items-center gap-3" data-testid={`portal-payment-${p.id}`}>
                <div className="flex-1 min-w-[140px]">
                  <div className="font-semibold text-slate-800">{monthLabel(p.month)}</div>
                  <div className="text-xs text-slate-400">{rupiah(p.amount_paid)} / {rupiah(p.amount_due)}{p.remaining > 0 && <span className="text-rose-500"> · sisa {rupiah(p.remaining)}</span>}</div>
                </div>
                <Badge variant="outline" className={`rounded-full ${PAYMENT_STATUS[p.status]?.cls}`}>{PAYMENT_STATUS[p.status]?.label}</Badge>
                {p.amount_paid > 0 && <Button size="sm" variant="outline" data-testid={`portal-receipt-${p.id}`} onClick={() => setReceipt(p)} className="rounded-lg h-8 border-emerald-300 text-emerald-700"><Printer className="w-3.5 h-3.5 mr-1" /> Kwitansi</Button>}
              </Card>
            ))}
          </div>
          <Card className="mt-3 p-4 rounded-2xl border-sky-100 bg-sky-50/50 text-xs text-slate-600">Pembayaran via transfer: <b>{settings.bank_name} {settings.bank_account}</b> a.n. {settings.bank_holder} · jatuh tempo tanggal {settings.due_day || 15} · Konfirmasi: WA {settings.phone}</Card>
        </section>
        <footer className="text-center text-xs text-slate-400 py-6">{settings.name} · {settings.address}</footer>
      </main>

      {receipt && (
        <div className="fixed inset-0 z-50 bg-black/50 overflow-auto p-4" data-testid="receipt-modal">
          <div className="no-print flex justify-end gap-2 max-w-[210mm] mx-auto mb-2">
            <Button onClick={() => window.print()} data-testid="portal-print-receipt" className="rounded-xl bg-emerald-500 hover:bg-emerald-600"><Printer className="w-4 h-4 mr-1" /> Cetak / Simpan PDF</Button>
            <Button variant="outline" onClick={() => setReceipt(null)} className="rounded-xl bg-white"><X className="w-4 h-4" /></Button>
          </div>
          <style>{`@media print { @page { size: 210mm 297mm; margin: 0; } }`}</style>
          <div id="print-root"><Kwitansi settings={settings} student={student} payment={receipt} /></div>
        </div>
      )}
    </div>
  );
}
